import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom"; // ✅ IMPORT useLocation
import { requestSeat, fetchMyRequests } from "../utils/api";
import { startCommunication } from "../utils/callHandler"; 
import RideRating from "./RideRating";

// Helper to safely grab IDs
const getId = (item) => {
  if (!item) return "";
  if (typeof item === 'string') return item;
  return item._id || item.id || item.toString();
};

export default function RideDetailsPassenger({ ride, user, driver }) {
  const navigate = useNavigate();
  const location = useLocation(); // ✅ Grab the current URL location
  const token = localStorage.getItem("travelo_token");
  const currentUserId = getId(user);

  const [loading, setLoading] = useState(false);
  const [myRequest, setMyRequest] = useState(null);
  const [seatsToBook, setSeatsToBook] = useState(1);
  const [showRating, setShowRating] = useState(false);

  const isConfirmed = (ride.confirmedPassengers || []).some(p => String(getId(p)) === String(currentUserId));
  const isFull = ride.seatsAvailable === 0;

  const passengerHasRated = (ride.ratings || ride.reviews || []).some((r) => {
    const raterId = getId(r.user) || getId(r.from) || getId(r.userId);
    return String(raterId) === String(currentUserId);
  });

  const checkMyStatus = useCallback(async () => {
    if (!token) return;
    try {
      const data = await fetchMyRequests(token);
      const list = Array.isArray(data) ? data : data.requests || [];
      const match = list.find(r => String(getId(r.ride) || r.rideId) === String(ride._id));
      if (match) setMyRequest(match);
    } catch (err) {
      console.error("Status check error:", err);
    }
  }, [ride._id, token]);

  useEffect(() => {
    checkMyStatus();
  }, [checkMyStatus]);

const handleBook = async () => {
    if (!token) return navigate("/phoneauth");
    if (myRequest) return alert("You already have a request for this ride.");

    try {
      setLoading(true);
      
      // ✅ NEW: Read from browser memory, NOT the URL!
      let searchFrom = localStorage.getItem("vyango_search_from") || "";
      let searchTo = localStorage.getItem("vyango_search_to") || "";

      // SMART OPTIMIZATION: Check if it's an exact match (ignoring case & spaces)
      const isExactFrom = searchFrom.trim().toLowerCase() === (ride.from || "").trim().toLowerCase();
      const isExactTo = searchTo.trim().toLowerCase() === (ride.to || "").trim().toLowerCase();

      // If it is the exact same route (or if memory had no search params), wipe the data
      if ((isExactFrom && isExactTo) || (!searchFrom && !searchTo)) {
        searchFrom = null;
        searchTo = null;
      }

      // Pass the cleaned data to the API function
      const res = await requestSeat(ride._id, seatsToBook, token, searchFrom, searchTo);
      
      setMyRequest(res.request || res);
      alert("✅ Request sent!");
    } catch (err) { 
      console.error("Booking API Error:", err);
      console.error("Backend Response:", err.response?.data);
      alert(err.response?.data?.error || err.message || "Error sending request"); 
    } finally { 
      setLoading(false); 
    }
  };


  const handleCancelNavigation = () => {
    const requestId = getId(myRequest);
    if (!requestId) return alert("Request ID missing. Please refresh.");
    
    navigate(`/ride/${ride._id}/cancel`, { 
      state: { ride: ride, request: myRequest, requestId: requestId } 
    });
  };

  const rideStatus = String(ride?.status || '').toLowerCase();
  const reqStatus = String(myRequest?.status || '').toLowerCase();

  return (
    <>
      {/* 1. RIDE COMPLETED */}
      {rideStatus === 'completed' && isConfirmed ? (
        <div className="rd-status-box completed-box">
          <h3>🏁 Journey Completed</h3>
          {passengerHasRated ? (
            <div className="rated-badge"><span className="icon">✔</span><p>You rated this trip</p></div>
          ) : (
            <button className="rd-btn primary full" onClick={() => setShowRating(true)}>⭐ Rate Driver</button>
          )}
        </div>
      ) : 

      /* 2. YOU CANCELLED */
      reqStatus === 'cancelled' ? (
        <div className="rd-status-box error-box">
          <h3>❌ You cancelled this booking</h3>
          <p>This booking is no longer active.</p>
          {myRequest?.cancelReason && <p style={{ fontStyle: 'italic', marginTop: '5px' }}>Reason: "{myRequest.cancelReason}"</p>}
        </div>
      ) :

      /* 3. DRIVER REJECTED */
      reqStatus === 'rejected' ? (
        <div className="rd-status-box error-box">
          <h3>😞 Request Rejected</h3>
          <p>The driver declined your request.</p>
        </div>
      ) :

      /* 4. DRIVER CANCELLED THE WHOLE RIDE */
      rideStatus === 'cancelled' ? (
        <div className="rd-status-box error-box">
          <h3>🚫 Ride Cancelled</h3>
          <p>The driver has cancelled this ride.</p>
          {ride.cancelReason && <p style={{ fontStyle: 'italic', marginTop: '5px' }}>Driver's Note: "{ride.cancelReason}"</p>}
        </div>
      ) :

      /* 5. CONFIRMED BOOKING */
      (isConfirmed || reqStatus === 'accepted' || reqStatus === 'confirmed') ? (
        <div className="rd-status-box confirmed-box">
          <h3>Booking Confirmed ✅</h3>
          <div className="rd-comm-row">
            <button className="rd-comm-btn chat" onClick={() => startCommunication(navigate, getId(driver), "chat")}>💬 Chat</button>
            <button className="rd-comm-btn call" onClick={() => startCommunication(navigate, getId(driver), "audio")}>📞 Call</button>
          </div>
          <button className="rd-link-danger mt-15" onClick={handleCancelNavigation}>Cancel Booking</button>
        </div>
      ) :

      /* 6. PENDING REQUEST */
      (myRequest && reqStatus === 'pending') ? (
        <div className="rd-status-box pending-box">
          <h3>⏳ Request Pending...</h3>
          <button className="rd-link-danger mt-10" onClick={handleCancelNavigation}>Cancel Request</button>
        </div>
      ) :

      /* 7. BOOKING UI (IF NOT FULL) */
      !isFull ? (
        <div className="rd-book-card">
          <div className="rd-counter-row">
            <span className="label">Seats:</span>
            <div className="rd-counter">
              <button type="button" onClick={() => setSeatsToBook(Math.max(1, seatsToBook-1))}>-</button>
              <span className="count">{seatsToBook}</span>
              <button type="button" onClick={() => setSeatsToBook(Math.min(ride.seatsAvailable, seatsToBook+1))}>+</button>
            </div>
          </div>
          <button className="rd-btn dark full" onClick={handleBook} disabled={loading}>
            {loading ? "Processing..." : "Request to Book"}
          </button>
        </div>
      ) : (
        /* 8. RIDE FULL */
        <div className="rd-status-box full-box"><p>Ride Full</p></div>
      )}

      {showRating && !passengerHasRated && (
        <RideRating 
          ride={ride} 
          userId={currentUserId} 
          driverName={driver.name} 
          driverId={getId(driver)} 
          onClose={() => setShowRating(false)} 
        />
      )}
    </>
  );
}